import { CreditsPermission } from '@idc-forms/permissions'
import { FileText } from 'lucide-react'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useCreditDocumentActions } from '../hooks/use-credit-document-actions'
import type { CreditDetail } from '../models/credit-list'
import { extractCreditDocuments } from '../operations/credit-detail-view'
import { creditDocumentAccess } from '../operations/credit-document-access'
import { CreditDocumentCard } from './credit-document-card'

type CreditDocumentsListProps = Pick<CreditDetail, 'id' | 'status' | 'stages'>

/**
 * La pestaña «Documentos» del expediente: todo lo que se adjuntó, de las siete etapas juntas.
 *
 * ── POR QUÉ ERA UNA LISTA MUERTA ─────────────────────────────────────────────
 *
 * Pintaba nombre y tamaño y **ninguna acción**, y no por olvido de la pantalla: no había forma de
 * pedir el archivo. El único endpoint v2 que firma una URL (`GET :id/url`) sirve **sólo
 * imágenes** —un PDF es 400— y la puerta que sí sirve PDFs es la de v1, que direcciona por el
 * uuid legacy que v2 nunca publica. Un adjunto de crédito se subía y no se podía volver a mirar
 * por ninguna de las dos. La descarga por `att_` (`GET :id/download-url`) es lo que lo destraba.
 *
 * ── LECTURA SIEMPRE, ESCRITURA MIENTRAS DURA EL PROCESO ──────────────────────
 *
 * Ver y descargar están disponibles también en un expediente aprobado — es cuando más se
 * consultan sus papeles. Renombrar y eliminar se apagan al aprobar, con el motivo a la vista: el
 * mismo candado que el servidor aplica, para que la pantalla no prometa algo que la API va a
 * negar con un 409.
 */
export function CreditDocumentsList({ id, status, stages }: CreditDocumentsListProps) {
  const documents = extractCreditDocuments(stages)
  const { has } = usePermissions()
  const { download, rename, remove } = useCreditDocumentActions(id)

  const { canWrite, writeBlockedReason } = creditDocumentAccess(
    status,
    has(CreditsPermission.ManageCreditDocuments)
  )

  if (documents.length === 0) {
    return (
      <div className="rounded-md border border-dashed p-4 text-center">
        <FileText aria-hidden="true" className="mx-auto mb-2 size-5 text-muted-foreground" />
        <p className="text-sm font-medium">Aún no hay documentos</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Los archivos aparecerán aquí cuando administración los adjunte.
        </p>
      </div>
    )
  }

  return (
    <ul className="space-y-2">
      {documents.map((document) => (
        <li key={document.id}>
          <CreditDocumentCard
            document={document}
            context={`${document.stageName} · ${document.fieldLabel}`}
            canWrite={canWrite}
            writeBlockedReason={writeBlockedReason}
            downloading={download.isPending && download.variables === document.id}
            renaming={rename.isPending && rename.variables?.id === document.id}
            removing={remove.isPending && remove.variables === document.id}
            onDownload={(documentId) => {
              download.mutate(documentId)
            }}
            onRename={(documentId, name) => {
              rename.mutate({ id: documentId, name })
            }}
            onRemove={(documentId) => {
              remove.mutate(documentId)
            }}
          />
        </li>
      ))}
    </ul>
  )
}
